import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import "./ChartNavigation.scss";

/**
 * Reusable Chart Navigation Component
 * Provides left/right navigation buttons for charts with scrollable data
 * Dynamically positions buttons based on chart's plot area boundaries
 *
 * @param {Object} props
 * @param {number} props.min - Current minimum visible index (X-axis)
 * @param {number} props.max - Current maximum visible index (X-axis)
 * @param {number} props.totalItems - Total number of items in the chart (X-axis)
 * @param {number} props.yMin - Current minimum visible index (Y-axis) - optional
 * @param {number} props.yMax - Current maximum visible index (Y-axis) - optional
 * @param {number} props.yTotalItems - Total number of items in the chart (Y-axis) - optional
 * @param {Function} props.onNavigateLeft - Callback when left button is clicked
 * @param {Function} props.onNavigateRight - Callback when right button is clicked
 * @param {Function} props.onNavigateUp - Callback when up button is clicked - optional
 * @param {Function} props.onNavigateDown - Callback when down button is clicked - optional
 * @param {Function} props.onReset - Callback when reset button is clicked
 * @param {string} props.className - Additional CSS class name
 * @param {boolean} props.enableYAxis - Enable Y-axis navigation (D-pad mode)
 * @param {number} props.rightOffset - Right offset adjustment in pixels (default: 0)
 */
const ChartNavigation = ({
  min,
  max,
  totalItems,
  yMin,
  yMax,
  yTotalItems,
  onNavigateLeft,
  onNavigateRight,
  onNavigateUp,
  onNavigateDown,
  onReset,
  className = "",
  enableYAxis = false,
  rightOffset = 0,
}) => {
  const isAtStart = min === 0;
  const isAtEnd = max >= totalItems;
  const isAtTop = yMin === 0; // At lowest indices (0-5)
  const isAtBottom = yMax >= yTotalItems; // At highest indices, max can equal totalItems for charts
  const navRef = useRef(null);
  const [position, setPosition] = useState({ bottom: 80, left: 70, right: 20 });
  const [isHoveringPlotArea, setIsHoveringPlotArea] = useState(false);
  const [plotAreaBounds, setPlotAreaBounds] = useState(null);

  useEffect(() => {
    // Find the chart SVG and extract plot area boundaries from clipPath
    const updatePosition = () => {
      if (navRef.current) {
        const chartContainer = navRef.current.parentElement;
        const svg = chartContainer?.querySelector(".k-chart-surface svg");
        const clipPath = svg?.querySelector("clipPath path");

        if (clipPath) {
          const pathData = clipPath.getAttribute("d");
          const coords = pathData.match(/[\d.]+/g);

          if (coords && coords.length >= 6) {
            const left = parseFloat(coords[0]);
            const top = parseFloat(coords[1]);
            const right = parseFloat(coords[2]);
            const bottom = parseFloat(coords[5]);

            const svgRect = svg.getBoundingClientRect();
            const containerRect = chartContainer.getBoundingClientRect();

            // Calculate position within plot area - positioned at bottom
            setPosition({
              bottom: svgRect.height - bottom + 65, // 65px above plot area bottom
              left: left,
              right: svgRect.width - right + rightOffset,
            });

            // Store plot area bounds for hover detection
            setPlotAreaBounds({
              left: left,
              top: top,
              right: right,
              bottom: bottom,
            });
          }
        }
      }
    };

    // Update position after chart renders
    const timer = setTimeout(updatePosition, 100);
    window.addEventListener("resize", updatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updatePosition);
    };
  }, [rightOffset]);

  useEffect(() => {
    // Track mouse position relative to plot area
    const handleMouseMove = (e) => {
      if (navRef.current && plotAreaBounds) {
        const chartContainer = navRef.current.parentElement;
        const svg = chartContainer?.querySelector(".k-chart-surface svg");

        if (svg) {
          const svgRect = svg.getBoundingClientRect();
          const mouseX = e.clientX - svgRect.left;
          const mouseY = e.clientY - svgRect.top;

          // Check if mouse is within plot area bounds
          const isInPlotArea =
            mouseX >= plotAreaBounds.left &&
            mouseX <= plotAreaBounds.right &&
            mouseY >= plotAreaBounds.top &&
            mouseY <= plotAreaBounds.bottom;

          setIsHoveringPlotArea(isInPlotArea);
        }
      }
    };

    const handleMouseLeave = () => {
      setIsHoveringPlotArea(false);
    };

    if (navRef.current && plotAreaBounds) {
      const chartContainer = navRef.current.parentElement;
      if (chartContainer) {
        chartContainer.addEventListener("mousemove", handleMouseMove);
        chartContainer.addEventListener("mouseleave", handleMouseLeave);

        return () => {
          chartContainer.removeEventListener("mousemove", handleMouseMove);
          chartContainer.removeEventListener("mouseleave", handleMouseLeave);
        };
      }
    }
  }, [plotAreaBounds]);

  // Determine what to show based on data
  const showXAxisNav = totalItems > max - min;
  const showYAxisNav = enableYAxis && yTotalItems > yMax - yMin;

  // Only show navigation if there's something to navigate
  if (!showXAxisNav && !showYAxisNav) {
    return null;
  }

  return (
    <div
      ref={navRef}
      className={`chart-navigation-buttons ${isHoveringPlotArea ? "visible" : ""} ${className}`}
      style={{
        bottom: `${position.bottom}px`,
        left: `${position.left}px`,
        right: `${position.right}px`,
      }}
    >
      <div className="nav-container">
        {/* Top button - show only if Y-axis navigation is needed */}
        {showYAxisNav && (
          <div className="nav-row">
            <Button
              title="Navigate chart up - view higher indices"
              className="chart-nav-btn-inside"
              onClick={onNavigateUp}
              disabled={isAtBottom}
            >
              <i className="fas fa-chevron-up"></i>
            </Button>
          </div>
        )}

        {/* Middle row - always has reset, conditionally shows left/right */}
        <div className="nav-row nav-middle">
          {showXAxisNav && (
            <Button
              title="Navigate chart left"
              className="chart-nav-btn-inside"
              onClick={onNavigateLeft}
              disabled={isAtStart}
            >
              <i className="fas fa-chevron-left"></i>
            </Button>
          )}
          <Button
            title="Reset chart to initial view"
            className="chart-nav-btn-inside chart-nav-refresh"
            onClick={onReset}
          >
            <i className="fas fa-sync-alt"></i>
          </Button>
          {showXAxisNav && (
            <Button
              title="Navigate chart right"
              className="chart-nav-btn-inside"
              onClick={onNavigateRight}
              disabled={isAtEnd}
            >
              <i className="fas fa-chevron-right"></i>
            </Button>
          )}
        </div>

        {/* Bottom button - show only if Y-axis navigation is needed */}
        {showYAxisNav && (
          <div className="nav-row">
            <Button
              title="Navigate chart down - view lower indices"
              className="chart-nav-btn-inside"
              onClick={onNavigateDown}
              disabled={isAtTop}
            >
              <i className="fas fa-chevron-down"></i>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartNavigation;
